import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { capitalise } from '../../../utilities/constants'
import { selectSkillName, setExperienceLevel, selectExperienceLevel, updateUserExperienceLevel, fetchSubskillsWithPolling, selectSubskillsLoading, selectTargetRole, selectIsRoleFlow } from '../../../redux/skillSlice'
import ProgressBar from '../../../components/ProgressBar'
import { selectInitSessionUserDetails } from '../../../redux/onboardingUserDetails'
import { Mixpanel } from '../../../config/Mixpanel'
import constants from '../../../utilities/constants'
import { selectIsAnalysisPageVisited, selectIsPricingPageVisited } from '../../../redux/newAnalysisSlice'

const OnboardingStep2 = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Get data from Redux
  const skillName = useSelector(selectSkillName) || 'Product Management'
  const currentExperienceLevel = useSelector(selectExperienceLevel)
  const userSessionDetails = useSelector(selectInitSessionUserDetails)
  const subskillsLoading = useSelector(selectSubskillsLoading)
  const targetRole = useSelector(selectTargetRole)
  const isRoleFlow = useSelector(selectIsRoleFlow)

  // Debug: Log current Redux values
  console.log('Current Experience Level in Redux:', currentExperienceLevel)
  console.log('Subskills Loading:', subskillsLoading)

  // State for selected experience level
  const [selectedLevel, setSelectedLevel] = useState(null)
  const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited);
  const isPricingPageVisited = useSelector(selectIsPricingPageVisited);

  // Experience level options
  const experienceOptions = [
    {
      id: 1,
      text: "I'm Completely New To This",
      emoji: '🚀',
      value: 'new'
    },
    {
      id: 2,
      text: 'I Know Basics',
      emoji: '📚',
      value: 'basics'
    },
    {
      id: 3,
      text: 'I Have Worked On Projects',
      emoji: '♻️',
      value: 'experienced'
    }
  ]


  useEffect(() => {
    if(isAnalysisPageVisited){
      navigate('/onboarding/analysis')
    }

    else if(isPricingPageVisited){
      navigate('/onboarding/pricing')
    }
    
  }, [dispatch, isAnalysisPageVisited, isPricingPageVisited, navigate])

  // Initialize component state from Redux when component mounts
  useEffect(() => {
    // Track page load
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP2_PAGE_LOADED, {
      skill: skillName,
      targetRole: targetRole,
      session_id: userSessionDetails?.session_id
    })

    // If we have an experience level stored in Redux, find and set the corresponding option
    if (currentExperienceLevel) {
      const experienceLevelMap = {
        'beginner': { id: 1, text: "I'm Completely New To This", emoji: '🚀', value: 'new' },
        'intermediate': { id: 2, text: 'I Know Basics', emoji: '📚', value: 'basics' },
        'advanced': { id: 3, text: 'I Have Worked On Projects', emoji: '♻️', value: 'experienced' }
      }
      
      const option = experienceLevelMap[currentExperienceLevel]
      if (option) {
        setSelectedLevel(option)
      }
    }
  }, [currentExperienceLevel, skillName, targetRole, userSessionDetails?.session_id])

  const handleOptionClick = async (option) => {
    console.log('Step 2 - Selected Experience Level:', option.text, 'Value:', option.value)

    // Track experience level selection
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP2_EXPERIENCE_LEVEL_SELECTED, {
      experience_level: option.id === 1 ? "beginner" : option.id === 2 ? "intermediate" : "advanced",
      skill: skillName,
      session_id: userSessionDetails?.session_id
    })

    try {
      // Update user experience level
      await dispatch(updateUserExperienceLevel({
        session_id: userSessionDetails?.session_id,
        experience_level: option.id === 1 ? "beginner" : option.id === 2 ? "intermediate" : "advanced"
      }))

      setSelectedLevel(option)

      // Save experience level to Redux
      dispatch(setExperienceLevel(option.id === 1 ? "beginner" : option.id === 2 ? "intermediate" : "advanced"))

      // Start polling for subskills
      console.log('Starting subskills polling...')
      // await dispatch(fetchSubskillsWithPolling({ 
      //   session_id: userSessionDetails?.session_id 
      // }))

      // Auto-navigate after selection with delay
      if (option.id === 1) {
        navigate('/onboarding/3')
      } else {
        navigate('/onboarding/loader-1')
      }


    } catch (error) {
      console.error('Error in handleOptionClick:', error)
    }
  }

  return (
    <div className='w-full h-full flex flex-col'>

      {/* Main content */}
      <div className='w-full mt-8 flex-1 flex flex-col justify-between items-center flex-1  w-full md:max-w-[45rem] mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative'>

        <div className='w-full flex flex-col justify-start items-center w-full'>
          <ProgressBar
            currentStep={isRoleFlow ? 4 : 2}
            totalSteps={isRoleFlow ? 8 : 6}
            stepText={`Step ${isRoleFlow ? 4 : 2}/${isRoleFlow ? 8 : 6} to  ${targetRole ? 'becoming ' + targetRole : 'mastering ' + capitalise(skillName)}`}
          />


          {/* Main content card */}
          <div className='w-full mt-4 flex flex-col p-[2px] rounded-[1.5rem]' style={{
            backgroundImage: `linear-gradient(
          to right,
          rgba(64, 106, 255, 0.3) 0%,
          rgba(64, 106, 255, 1) 56%,
          rgba(64, 106, 255, 0.3) 100%
        )`
          }}>
            {/* Main content card */}
            <div className='flex flex-col p-6 rounded-[1.5rem]  shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] w-full relative overflow-hidden' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>
              <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px]"
                style={{ backgroundImage: `url('https://assets.learntube.ai/files/personalise/web.webp')` }} />
              {/* Content */}
              <div className='flex flex-col gap-6 relative z-10'>

                {/* Header */}
                <div className='flex flex-col'>
                  <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                    Have you worked with {capitalise(skillName)} before?
                  </h1>
                </div>

                {/* Experience level options */}
                <div className='flex flex-col gap-3 w-full'>
                  {experienceOptions.map((option, index) => {
                    const isSelected = selectedLevel?.value === option.value

                    return (
                      <div
                        key={index}
                        className={`${isSelected
                            ? 'bg-[#7FC241]/20 border-[#7FC241] shadow-[0px_0px_2px_0px_#7fc241]'
                            : 'bg-[#406AFF]/15 border-[#406AFF]/70 hover:bg-[#406AFF]/25 hover:border-[#406AFF]'
                          } border border-solid flex gap-2 items-center p-4 rounded-lg cursor-pointer transition-all duration-200 w-full group`}
                        onClick={() => handleOptionClick(option)}
                      >
                        {/* Radio button */}
                        <div className='relative w-[14px] h-[14px] shrink-0'>
                          <div className={`w-[14px] h-[14px] rounded-full border-2 ${isSelected 
                              ? 'border-[#7FC241]' 
                              : 'border-[#70AEFF]'
                            }`}>
                            <div className={`w-[6px] h-[6px] rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all ${isSelected 
                                ? 'bg-[#7FC241] opacity-100' 
                                : 'bg-[#70AEFF] opacity-0 '
                              }`}></div>
                          </div>
                        </div>

                        {/* Option content */}
                        <div className='flex flex-1 items-center justify-between text-[14px] text-white'>
                          <span className='capitalize'>{option.text}</span>
                          <span>{option.emoji}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom certification info */}
        <div className='flex flex-col gap-3 items-center justify-center w-full mt-6'>
          {/* Company logos */}
          <div className='flex items-center pr-2'>
            <div className='w-[66px] h-[28px]'>
              <img alt="Company logo" className='w-full h-full object-cover' src="https://assets.learntube.ai/files/personalise/Frame%20427321976%20(2).webp" />
            </div>
          </div>

          {/* Certification text */}
          <div className='text-center'>
            <p className='font-medium italic text-[0.75rem] text-[#83BEFF] leading-[1.4]'>
              +1,100 companies recognise this certificate for roles in {capitalise(skillName)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingStep2